import { SocialProofCardProps } from '@/components/_atoms/social-proof-cards/SocialProofCard';
import SocialProofQuoteCard from '@/components/_atoms/social-proof-cards/SocialProofQuoteCard';
import SocialProofQuoteWithStarsCard from '@/components/_atoms/social-proof-cards/SocialProofQuoteWithStarsCard';
import SocialProofReviewSiteCard from '@/components/_atoms/social-proof-cards/SocialProofReviewSiteCard';
import SocialProofSocialMediaCard from '@/components/_atoms/social-proof-cards/SocialProofSocialMediaCard';
import SocialProofStatCard from '@/components/_atoms/social-proof-cards/SocialProofStatCard';
import SocialProofCardCarousel from '@/components/_organisms/SocialProofCardCarousel';
import Image from '@/components/Image';
import { ModuleSocialProofCardCarouselFragment } from '@/graphql';
import { getSingleLink } from '@/lib/craft-utils';
import gtm from '@/lib/gtm';
import { parseHtml } from '@/lib/parse-html';
import { getSingle, maybeNotSet, notEmpty } from '@/lib/typescript-helpers';
import { FC } from 'react';

interface SocialProofCardCarouselModuleProps extends PropsWithClassName {
  props: CraftModuleFragment<ModuleSocialProofCardCarouselFragment>;
}

const SocialProofCardCarouselModule: FC<SocialProofCardCarouselModuleProps> = ({ props, className }) => {
  if (Array.isArray(props)) {
    props = props[0];
  }

  if (!props) return null;

  return (
    <SocialProofCardCarousel heading={props.heading ?? undefined} className={className}>
      {props.socialProofCards.filter(notEmpty).map((card, index) => {
        const cardCta = 'cta' in card ? getSingleLink(card.cta) : undefined;

        const cta: SocialProofCardProps['cta'] = cardCta
          ? {
            text: cardCta.text,
            href: cardCta.url,
            newTab: true,
            onClick() {
              gtm.track('click', {
                element_type: 'link',
                element_label: `Card | Position ${index + 1} | ${maybeNotSet(cardCta.text)}`,
                location: 'Social Proof Card Carousel',
              });
            },
          }
          : undefined;

        switch (card.__typename) {
          case 'socialProofCards_quote_BlockType':
            const quote = getSingle(card.quote);

            if (!quote) return null;

            const customer = getSingle(quote.customer);
            const quoteLogo = getSingle(customer?.logoColor);
            const quoteAvatar = getSingle(quote.authorPhoto);

            return (
              <SocialProofQuoteCard
                key={`social-proof-quote-card-${quote.authorName?.replace(/\s+/g, '-') ?? 'unknown'}-${index}`}
                name={quote.authorName ?? ''}
                title={quote.authorTitle ?? ''}
                logo={
                  quoteLogo ? (
                    <Image
                      src={quoteLogo.url!}
                      width={quoteLogo.width!}
                      height={quoteLogo.height!}
                      alt={`${customer?.title} logo`}
                    />
                  ) : null
                }
                avatar={
                  quoteAvatar ? (
                    <Image
                      src={quoteAvatar.url!}
                      width={quoteAvatar.width!}
                      height={quoteAvatar.height!}
                      alt={quote.authorName ?? ''}
                    />
                  ) : null
                }
                cta={cta}
              >
                {parseHtml(quote.quote)}
              </SocialProofQuoteCard>
            );

          case 'socialProofCards_statistic_BlockType':
            const statLogo = getSingle(getSingle(card.logo)?.logo);

            return (
              <SocialProofStatCard
                key={`social-proof-stat-card-${card.label?.replace(/\s+/g, '-') ?? 'unknown'}-${index}`}
                value={card.statistic!}
                label={card.label!}
                logo={
                  statLogo ? (
                    <Image
                      src={statLogo.url!}
                      width={statLogo.width!}
                      height={statLogo.height!}
                      alt={statLogo.altText ?? ''}
                    />
                  ) : null
                }
                cta={cta}
              >
                {parseHtml(card.text)}
              </SocialProofStatCard>
            );

          case 'socialProofCards_socialMedia_BlockType':
            const socialAvatar = getSingle(card.avatar);

            return (
              <SocialProofSocialMediaCard
                key={`social-proof-social-media-card-${card.socialMediaPlatform as 'twitter' | 'linkedin'}-${index}`}
                logo={card.socialMediaPlatform as 'twitter' | 'linkedin'}
                name={card.quoteeName ?? ''}
                handle={card.quoteeHandle ?? ''}
                avatar={
                  socialAvatar ? (
                    <Image
                      src={socialAvatar.url!}
                      width={socialAvatar.width!}
                      height={socialAvatar.height!}
                      alt={card.quoteeName ?? ''}
                    />
                  ) : null
                }
                cta={cta}
              >
                {parseHtml(card.text)}
              </SocialProofSocialMediaCard>
            );

          case 'socialProofCards_quoteWithStars_BlockType':
            return (
              <SocialProofQuoteWithStarsCard
                key={`social-proof-quote-with-stars-card-${card.stars}-${index}`}
                stars={card.stars}
              >
                {parseHtml(card.richTextQuote)}
              </SocialProofQuoteWithStarsCard>
            );

          case 'socialProofCards_reviewSite_BlockType':
            const reviewerImage = getSingle(card.reviewerImage);

            return (
              <SocialProofReviewSiteCard
                key={`social-proof-review-site-card-${card.reviewerName?.replace(/\s+/g, '-') ?? 'unknown'}-${index}`}
                name={card.reviewerName ?? ''}
                nameSubhead={card.reviewerSubhead ?? ''}
                image={
                  reviewerImage ? (
                    <Image
                      src={reviewerImage.url!}
                      width={reviewerImage.width!}
                      height={reviewerImage.height!}
                      alt={card.reviewerName ?? ''}
                    />
                  ) : null
                }
                stars={card.stars}
                excerpt={card.featuredExcerpt ?? undefined}
                cta={cta}
              >
                {parseHtml(card.excerpt)}
              </SocialProofReviewSiteCard>
            );

          default:
            return null;
        }
      })}
    </SocialProofCardCarousel>
  );
};

export default SocialProofCardCarouselModule;
